import React, { useCallback, useEffect } from 'react';
import SideBar from '../../../components/SideBar';
import NavBar from '../../../components/NavBar';
import Footer from '../../../components/Footer';
import { Button, Select, Upload, Card, Tag, Divider, Empty, Spin } from 'antd';
import axiosInstance from '../../../variables/api/axiosInstance';
import axiosMultiPartDataInstance from '../../../variables/api/axiosMultiPartDatainstance';
import { SendOutlined, UploadOutlined, CheckCircleOutlined, ClockCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { LOGGED_USER } from '../../../variables/constants';
import { toast } from 'react-toastify';

const NouvelleConventionCollaboration = () => {
    const [agreements, setAgreements] = React.useState([]);
    const [selectedCompagnies, setSelectedCompagnies] = React.useState([]);
    const [assurances, setAssurances] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [validAgreements, setValidAgreements] = React.useState([]);
    const [pendingAgreements, setPendingAgreements] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    const courtierCodeId =
        LOGGED_USER?.agence?.codeId ||
        LOGGED_USER?.agence?.code ||
        LOGGED_USER?.agence?.codeAgence;

    const fetchAgencyData = useCallback(() => {
        if (!courtierCodeId) {
            toast.error("Impossible d'identifier votre agence.");
            setLoading(false);
            return;
        }

        setLoading(true);

        Promise.all([
            axiosInstance.get(`/api/agreement/courtier/${courtierCodeId}/available-companies`),
            axiosInstance.get(`/api/agence/${courtierCodeId}`)
        ])
            .then(([availableRes, agenceRes]) => {
                const availableCompanies = Array.isArray(availableRes.data) ? availableRes.data : [];
                setAssurances(availableCompanies);

                const agenceData = agenceRes.data;
                const validAgrs = agenceData?.validAgrements || [];
                const allAgrs = agenceData?.agrements || [];

                setValidAgreements(validAgrs);

                const pendingAgrs = allAgrs.filter(ag =>
                    !validAgrs.some(vag => vag.agreementId === ag.agreementId)
                );
                setPendingAgreements(pendingAgrs);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Impossible de récupérer les données. Veuillez réessayer.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [courtierCodeId]);

    const handleSelectCompagniesChange = (value) => {
        setAgreements((prevAgreements) =>
            value.map((element) => {
                const company = JSON.parse(element);
                const existing = prevAgreements.find((ag) => ag.company.code === company.code);

                return {
                    company,
                    document: existing?.document || [],
                };
            }),
        );
        setSelectedCompagnies(value);
    };

    useEffect(() => {
        fetchAgencyData();
    }, [fetchAgencyData]);

    const onChangeBeforeUploadFileSelect = (file, code) => {
        setAgreements((prevAgreements) => {
            const nextAgreements = [...prevAgreements];
            const index = nextAgreements.findIndex((ag) => ag.company.code === code);

            if (index !== -1) {
                file.status = "done";
                nextAgreements[index] = {
                    ...nextAgreements[index],
                    document: file,
                };
            }

            return nextAgreements;
        });
    };

    const resetAgreementDocument = (companyCode) => {
        setAgreements((prev) =>
            prev.map((ag) =>
                ag.company.code === companyCode ? { ...ag, document: [] } : ag,
            ),
        );
    };

    const onChangeFileSelect = (e, code) => {
        if (e.file.status === "removed") {
            resetAgreementDocument(code);
        }
    };

    const handleSubmit = async () => {
        if (!selectedCompagnies.length) {
            toast.info("Veuillez sélectionner au moins une compagnie.");
            return;
        }

        const missingDocuments = agreements.filter(
            (ag) => !ag.document || (Array.isArray(ag.document) && !ag.document.length),
        );

        if (missingDocuments.length) {
            toast.error("Veuillez joindre les conventions pour chaque compagnie sélectionnée.");
            return;
        }

        if (!courtierCodeId) {
            toast.error("Impossible d'identifier votre agence. Veuillez réessayer.");
            return;
        }

        try {
            setSubmitting(true);

            const data = new FormData();

            agreements.forEach((ag) => {
                const file = ag.document;

                if (!file) {
                    return;
                }

                const extension = file?.name?.substring(
                    file?.name?.lastIndexOf(".") + 1,
                    file?.name?.length,
                );

                const filename = `Agreement_${ag?.company?.code}_${LOGGED_USER?.agence?.codeAgence || "AGENCE"}.${extension}`;

                data.append("fichiers", file, filename);
                data.append("compagnies", ag.company.code);
            });

            const response = await axiosMultiPartDataInstance.post(
                `/api/agreement/courtier/${courtierCodeId}`,
                data,
            );

            const createdAgreements = Array.isArray(response.data)
                ? response.data
                : [];

            toast.success(
                createdAgreements.length > 1
                    ? "Conventions téléversées. Elles seront prises en compte par l'administrateur."
                    : "Convention téléversée. Elle sera prise en compte par l'administrateur.",
            );
            setAgreements([]);
            setSelectedCompagnies([]);
            fetchAgencyData();
        } catch (error) {
            console.log(error);
            const backendMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message;
            toast.error(
                backendMessage
                    ? `Erreur lors de l'envoi des conventions: ${backendMessage}`
                    : "Erreur lors de l'envoi des conventions. Veuillez réessayer.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '50px' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    {/* Section des agréments validés et en attente */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                        gap: '20px',
                                        marginBottom: '24px'
                                    }}>
                                        {/* Compagnies Actives */}
                                        <Card
                                            style={{
                                                borderRadius: '12px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                border: '1px solid #b7eb8f',
                                                background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)'
                                            }}
                                            bodyStyle={{ padding: '20px' }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: '16px'
                                            }}>
                                                <div style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    borderRadius: '12px',
                                                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: '12px',
                                                    boxShadow: '0 4px 8px rgba(82,196,26,0.25)'
                                                }}>
                                                    <CheckCircleOutlined style={{
                                                        fontSize: '24px',
                                                        color: '#fff'
                                                    }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontSize: '18px',
                                                        fontWeight: '700',
                                                        color: '#242166',
                                                        marginBottom: '4px'
                                                    }}>
                                                        Compagnies Actives
                                                    </div>
                                                    <div style={{
                                                        fontSize: '24px',
                                                        fontWeight: '700',
                                                        color: '#52c41a'
                                                    }}>
                                                        {validAgreements.length}
                                                    </div>
                                                </div>
                                            </div>
                                            {validAgreements.length > 0 ? (
                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                    maxHeight: '180px',
                                                    overflowY: 'auto',
                                                    padding: '4px'
                                                }}>
                                                    {validAgreements.map((ag, idx) => (
                                                        <Tag
                                                            key={idx}
                                                            icon={<CheckCircleOutlined />}
                                                            color="success"
                                                            style={{
                                                                fontSize: '13px',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #b7eb8f',
                                                                margin: 0
                                                            }}
                                                        >
                                                            {ag.compagnie?.raisonSociale || ag.compagnie?.nom || 'Compagnie'}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    textAlign: 'center',
                                                    color: '#8c8c8c',
                                                    fontSize: '13px',
                                                    padding: '12px 0'
                                                }}>
                                                    Aucun agrément validé
                                                </div>
                                            )}
                                        </Card>

                                        {/* En Cours de Validation */}
                                        <Card
                                            style={{
                                                borderRadius: '12px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                border: '1px solid #ffe58f',
                                                background: 'linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)'
                                            }}
                                            bodyStyle={{ padding: '20px' }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: '16px'
                                            }}>
                                                <div style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    borderRadius: '12px',
                                                    background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: '12px',
                                                    boxShadow: '0 4px 8px rgba(250,173,20,0.25)'
                                                }}>
                                                    <ClockCircleOutlined style={{
                                                        fontSize: '24px',
                                                        color: '#fff'
                                                    }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontSize: '18px',
                                                        fontWeight: '700',
                                                        color: '#242166',
                                                        marginBottom: '4px'
                                                    }}>
                                                        En Validation
                                                    </div>
                                                    <div style={{
                                                        fontSize: '24px',
                                                        fontWeight: '700',
                                                        color: '#faad14'
                                                    }}>
                                                        {pendingAgreements.length}
                                                    </div>
                                                </div>
                                            </div>
                                            {pendingAgreements.length > 0 ? (
                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                    maxHeight: '180px',
                                                    overflowY: 'auto',
                                                    padding: '4px'
                                                }}>
                                                    {pendingAgreements.map((ag, idx) => (
                                                        <Tag
                                                            key={idx}
                                                            icon={<ClockCircleOutlined />}
                                                            color="warning"
                                                            style={{
                                                                fontSize: '13px',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #ffe58f',
                                                                margin: 0
                                                            }}
                                                        >
                                                            {ag.compagnie?.raisonSociale || ag.compagnie?.nom || 'Compagnie'}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    textAlign: 'center',
                                                    color: '#8c8c8c',
                                                    fontSize: '13px',
                                                    padding: '12px 0'
                                                }}>
                                                    Aucune convention en attente
                                                </div>
                                            )}
                                        </Card>
                                    </div>

                                    {/* Section de soumission de nouvelles conventions */}
                                    <Card
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 4px 12px rgba(36,33,102,0.12)',
                                            border: '1px solid #e8e8e8',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
                                        }}
                                    >
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '40px 20px'
                                        }}>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #242166 0%, #4a47a3 100%)',
                                                marginBottom: '24px',
                                                boxShadow: '0 4px 12px rgba(36,33,102,0.3)'
                                            }}>
                                                <FileTextOutlined style={{ fontSize: '40px', color: '#fff' }} />
                                            </div>

                                            <h2 style={{
                                                fontSize: '28px',
                                                fontWeight: '700',
                                                color: '#242166',
                                                marginBottom: '16px',
                                                lineHeight: '1.4'
                                            }}>
                                                Nouvelle Convention de Collaboration
                                            </h2>

                                            <p style={{
                                                fontSize: '16px',
                                                color: '#595959',
                                                marginBottom: '32px',
                                                maxWidth: '700px',
                                                margin: '0 auto 32px',
                                                lineHeight: '1.6'
                                            }}>
                                                Soumettez vos conventions avec de nouvelles compagnies d'assurance pour étendre votre réseau de partenariats
                                            </p>

                                            <div style={{
                                                maxWidth: '800px',
                                                margin: '0 auto'
                                            }}>
                                                <Select
                                                    mode="multiple"
                                                    size="large"
                                                    style={{
                                                        width: '100%',
                                                        marginBottom: '24px'
                                                    }}
                                                    placeholder="Sélectionnez les nouvelles compagnies d'assurance"
                                                    onChange={handleSelectCompagniesChange}
                                                    options={assurances?.map((c, id) => ({
                                                        label: c.raisonSociale,
                                                        value: JSON.stringify(c),
                                                        key: id,
                                                    }))}
                                                    value={selectedCompagnies}
                                                    notFoundContent={
                                                        <Empty
                                                            description="Aucune nouvelle compagnie disponible"
                                                            style={{ padding: '20px' }}
                                                        />
                                                    }
                                                />

                                                {selectedCompagnies?.map((sc, id) => {
                                                    const company = JSON.parse(sc);
                                                    return (
                                                        <div
                                                            key={id}
                                                            style={{
                                                                marginBottom: '20px',
                                                                padding: '20px',
                                                                background: '#fff',
                                                                borderRadius: '8px',
                                                                border: '2px dashed #d9d9d9',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            <div style={{
                                                                fontSize: '16px',
                                                                fontWeight: '600',
                                                                color: '#242166',
                                                                marginBottom: '12px'
                                                            }}>
                                                                {company.raisonSociale}
                                                            </div>
                                                            <Upload
                                                                name="agreement"
                                                                beforeUpload={(file) => {
                                                                    onChangeBeforeUploadFileSelect(file, company.code);
                                                                    return false;
                                                                }}
                                                                onChange={(e) => onChangeFileSelect(e, company.code)}
                                                                onRemove={() => resetAgreementDocument(company.code)}
                                                                listType="picture"
                                                                maxCount={1}
                                                                accept=".pdf,.jpg,.jpeg,.png"
                                                            >
                                                                <Button
                                                                    icon={<UploadOutlined />}
                                                                    size="large"
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '48px',
                                                                        borderRadius: '8px',
                                                                        borderColor: '#242166',
                                                                        color: '#242166',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    Téléverser la convention
                                                                </Button>
                                                            </Upload>
                                                        </div>
                                                    );
                                                })}

                                                {selectedCompagnies.length > 0 && (
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        icon={<SendOutlined />}
                                                        loading={submitting}
                                                        disabled={submitting}
                                                        onClick={handleSubmit}
                                                        style={{
                                                            height: '50px',
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                            borderRadius: '8px',
                                                            background: 'linear-gradient(135deg, #242166 0%, #4a47a3 100%)',
                                                            border: 'none',
                                                            boxShadow: '0 4px 12px rgba(36,33,102,0.3)',
                                                            minWidth: '200px'
                                                        }}
                                                    >
                                                        {submitting ? "Envoi en cours..." : "Soumettre les conventions"}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                </>
                            )}
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default NouvelleConventionCollaboration;